<template>
	<div class="meta-label-with-action">
		<span class="meta-label">Sistema de cierre</span>
		<v-menu ref="closureMenu" offset-y offset-overflow :close-on-content-click="false" v-model="menuOpen" max-width="320" :max-height="menuMaxHeight">
			<template v-slot:activator="{ on, attrs }">
				<button class="icon-action-btn ml-2" v-bind="attrs" v-on="on" title="Editar perfiles de cierre">
					<v-icon size="14" color="#2D4B95">mdi-lock-outline</v-icon>
				</button>
			</template>

			<div class="closure-menu">
				<!-- Header -->
				<div class="closure-menu-header">
					<p class="closure-menu-title">Perfiles de cierre</p>
					<button class="icon-btn-light" @click="menuOpen = false">
						<v-icon size="14" color="white">mdi-close</v-icon>
					</button>
				</div>

				<!-- Toggle -->
				<div class="closure-toggle-banner" :class="localUseAdvancedClosure ? 'active' : ''" @click="localUseAdvancedClosure = !localUseAdvancedClosure">
					<div class="closure-toggle-icon">
						<v-icon size="16" :color="localUseAdvancedClosure ? '#2D4B95' : '#9CA3AF'">
							{{ localUseAdvancedClosure ? 'mdi-shield-account' : 'mdi-cog-outline' }}
						</v-icon>
					</div>
					<div class="closure-toggle-text">
						<span class="closure-toggle-title">{{ localUseAdvancedClosure ? 'Cierre por perfiles' : 'Configuración del proyecto' }}</span>
						<span class="closure-toggle-desc">
							{{ localUseAdvancedClosure
								? 'Los perfiles seleccionados deben aprobar el cierre.'
								: 'Usa el cierre configurado en el proyecto (tradicional o por perfiles).' }}
						</span>
					</div>
					<v-switch
						:input-value="localUseAdvancedClosure"
						dense hide-details readonly
						color="#2D4B95"
						class="closure-switch"
					/>
				</div>

				<!-- Profile pills -->
				<div v-if="localUseAdvancedClosure" class="closure-profiles-grid">
					<div
						v-for="profile in availableClosureProfiles"
						:key="profile.id"
						class="closure-profile-pill"
						:class="{ selected: isSelected(profile.id) }"
						@click="toggleProfile(profile.id)"
					>
						<div class="closure-pill-avatar" :style="avatarStyle(profile)">
							{{ profile.name.charAt(0).toUpperCase() }}
						</div>
						<span class="closure-pill-name">{{ profile.name }}</span>
						<v-icon v-if="isSelected(profile.id)" size="12" color="#2D4B95" class="closure-pill-check">mdi-check</v-icon>
					</div>
				</div>

				<!-- Selected summary -->
				<div v-if="localUseAdvancedClosure && localSelectedProfiles.length > 0" class="closure-selected-summary">
					<v-chip
						v-for="pid in localSelectedProfiles"
						:key="'chip-'+pid"
						x-small
						close
						color="#EEF2FF"
						text-color="#2D4B95"
						@click:close="toggleProfile(pid)"
					>
						{{ profileName(pid) }}
					</v-chip>
				</div>

				<!-- Actions -->
				<div class="closure-menu-actions">
					<button class="closure-cancel-btn" @click="menuOpen = false">Cancelar</button>
					<button class="closure-save-btn" :disabled="saving" @click="save">
						<v-progress-circular v-if="saving" :size="12" :width="2" indeterminate color="white" />
						<span v-else>Guardar</span>
					</button>
				</div>
			</div>
		</v-menu>
	</div>
</template>

<script>
import { updateTask } from '@/helpers/api/tasks'

export default {
	name: 'TaskClosureProfileEdit',
	props: {
		tarea: {
			type: Object,
			required: true
		},
		loading: {
			type: Boolean,
			default: false
		}
	},
	data: () => ({
		menuOpen: false,
		localUseAdvancedClosure: false,
		localSelectedProfiles: [],
		saving: false,
		avatarColors: ['#2D4B95', '#3D6BD4', '#E67E22', '#27AE60', '#8E44AD', '#E74C3C', '#16A085', '#D35400']
	}),
	computed: {
		menuMaxHeight() {
			return Math.min(480, window.innerHeight - 120)
		},
		availableClosureProfiles() {
			const responsable = { id: -2, name: 'Responsable', description: 'Responsable del ítem' }
			const creator = { id: -1, name: 'Creador', description: 'Creador del ítem' }
			const projectProfiles = this.$store.projectsStore.state.projectProfiles || []
			return [responsable, creator, ...projectProfiles]
		}
	},
	watch: {
		menuOpen(val) {
			if (val) this.initFromTarea()
		},
		localUseAdvancedClosure() {
			this.$nextTick(() => {
				if (this.$refs.closureMenu) this.$refs.closureMenu.updateDimensions()
			})
		}
	},
	methods: {
		initFromTarea() {
			const config = this.tarea.closure_config
			if (config && Array.isArray(config.advanced_closure_profiles) && config.advanced_closure_profiles.length > 0) {
				this.localUseAdvancedClosure = true
				this.localSelectedProfiles = [...config.advanced_closure_profiles]
			} else {
				this.localUseAdvancedClosure = false
				this.localSelectedProfiles = []
			}
		},
		isSelected(id) {
			return this.localSelectedProfiles.includes(id)
		},
		toggleProfile(id) {
			const idx = this.localSelectedProfiles.indexOf(id)
			if (idx === -1) {
				this.localSelectedProfiles.push(id)
			} else {
				this.localSelectedProfiles.splice(idx, 1)
			}
		},
		profileName(id) {
			if (id === -1) return 'Creador'
			if (id === -2) return 'Responsable'
			const found = (this.$store.projectsStore.state.projectProfiles || []).find(p => p.id === id)
			return found ? found.name : `Perfil ${id}`
		},
		avatarStyle(profile) {
			const idx = Math.abs(profile.id) % this.avatarColors.length
			const isSelected = this.isSelected(profile.id)
			return {
				backgroundColor: isSelected ? this.avatarColors[idx] : '#E5E8EF',
				color: isSelected ? 'white' : '#6B7280',
				width: '26px', height: '26px',
				borderRadius: '50%',
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				fontSize: '11px', fontWeight: '700',
				flexShrink: '0', transition: 'background 0.2s'
			}
		},
		async save() {
			this.saving = true
			const payload = { id: this.tarea.id }

			if (this.localUseAdvancedClosure && this.localSelectedProfiles.length > 0) {
				payload.task_closure_profiles = this.localSelectedProfiles.map(id => parseInt(id))
			} else {
				payload.task_closure_profiles = null
			}

			const res = await updateTask(payload)
			if (res.code === 200) {
				this.$emit('updated', res.task)
				this.menuOpen = false
			}
			this.saving = false
		}
	}
}
</script>

<style scoped>
.meta-label-with-action {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 8px;
	margin-bottom: 8px;
}
.meta-label {
	font-size: 11px;
	font-weight: 600;
	color: #6B7280;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin: 0;
}
.icon-action-btn {
	width: 28px; height: 28px; border-radius: 7px;
	background: #EEF2FF; border: none; cursor: pointer;
	display: inline-flex; align-items: center; justify-content: center;
	transition: background 0.12s;
}
.icon-action-btn:hover { background: #dde5f9; }

/* Menu container */
.closure-menu {
	background: white;
	border-radius: 12px;
	overflow-y: auto;
	box-shadow: 0 8px 24px rgba(0,0,0,0.12);
	min-width: 280px;
}
.closure-menu-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: #2D4B95;
	padding: 10px 14px;
}
.closure-menu-title {
	font-size: 13px;
	font-weight: 600;
	color: white;
	margin: 0;
}
.icon-btn-light {
	background: rgba(255,255,255,0.15);
	border: none; cursor: pointer;
	width: 24px; height: 24px;
	border-radius: 6px;
	display: flex; align-items: center; justify-content: center;
	transition: background 0.12s;
}
.icon-btn-light:hover { background: rgba(255,255,255,0.25); }

/* Toggle banner */
.closure-toggle-banner {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px 14px;
	cursor: pointer;
	background: #F8F9FC;
	border-bottom: 1px solid #E5E8EF;
	transition: background 0.15s;
}
.closure-toggle-banner:hover { background: #EEF2FF; }
.closure-toggle-banner.active { background: #EEF2FF; }
.closure-toggle-icon {
	width: 32px; height: 32px;
	border-radius: 8px;
	background: white;
	border: 1px solid #E5E8EF;
	display: flex; align-items: center; justify-content: center;
	flex-shrink: 0;
}
.closure-toggle-text {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 1px;
}
.closure-toggle-title {
	font-size: 12px;
	font-weight: 600;
	color: #1A1D23;
}
.closure-toggle-desc {
	font-size: 10px;
	color: #6B7280;
	line-height: 1.4;
}
.closure-switch {
	margin: 0;
	padding: 0;
	flex-shrink: 0;
}

/* Profile pills */
.closure-profiles-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	padding: 12px 14px 6px;
}
.closure-profile-pill {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 4px 10px 4px 5px;
	border-radius: 20px;
	border: 1.5px solid #E5E8EF;
	cursor: pointer;
	background: #F8F9FC;
	transition: border-color 0.15s, background 0.15s;
	flex-direction: row;
}
.closure-profile-pill:hover { border-color: #3D6BD4; background: #EEF2FF; }
.closure-profile-pill.selected {
	border-color: #2D4B95;
	background: #EEF2FF;
}
.closure-pill-avatar {
	transition: background 0.2s;
}
.closure-pill-name {
	font-size: 11px;
	font-weight: 500;
	color: #374151;
}
.closure-pill-check {
	margin-left: 2px;
}

/* Selected chips */
.closure-selected-summary {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	padding: 4px 14px 10px;
}

/* Actions */
.closure-menu-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 10px 14px;
	border-top: 1px solid #E5E8EF;
	background: #F8F9FC;
}
.closure-cancel-btn {
	background: none;
	border: 1px solid #D1D5DB;
	border-radius: 7px;
	padding: 5px 12px;
	font-size: 12px;
	color: #6B7280;
	cursor: pointer;
	transition: background 0.12s;
}
.closure-cancel-btn:hover { background: #F3F4F6; }
.closure-save-btn {
	background: #2D4B95;
	color: white;
	border: none;
	border-radius: 7px;
	padding: 5px 14px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s;
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 60px;
	justify-content: center;
}
.closure-save-btn:hover:not(:disabled) { background: #243d7a; }
.closure-save-btn:disabled { opacity: 0.6; cursor: default; }
</style>
